import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const SeverancesCharts = ({ 
  severancesData, 
  onSortUnresolvedViolations,
  unresolvedViolationsSortField, 
  unresolvedViolationsSortDirection,
  onResetUnresolvedViolations,
  severancesFilter,
  setSeverancesFilter,
  resetSeverancesFilter,
  hiddenSeverancesLines,
  setHiddenSeverancesLines,
  resetHiddenSeverancesLines,
  hiddenSeveranceVolumeLines,
  setHiddenSeveranceVolumeLines,
  resetHiddenSeveranceVolumeLines
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check if severances have data for each filter
  const severancesDataUnresolved = severancesData.filter(record => {
    const resolvedDateColumn = record['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                             record.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
    return !resolvedDateColumn || !record[resolvedDateColumn] || record[resolvedDateColumn].toString().trim() === '';
  });
  const severancesDataAll = severancesData; // All violations
  const hasSeverancesUnresolved = severancesDataUnresolved.length > 0;
  const hasSeverancesAll = severancesDataAll.length > 0;
  const hasAnySeverances = hasSeverancesUnresolved || hasSeverancesAll;

  // Ensure the current filter is valid (has data)
  // If current filter has no data, switch to the other one that has data
  const validSeverancesFilter = (() => {
    if (severancesFilter === 'unresolved' && !hasSeverancesUnresolved && hasSeverancesAll) {
      return 'all';
    }
    if (severancesFilter === 'all' && !hasSeverancesAll && hasSeverancesUnresolved) {
      return 'unresolved';
    }
    return severancesFilter;
  })();

  // Update filter state if it's invalid
  useEffect(() => {
    if (validSeverancesFilter !== severancesFilter) {
      setSeverancesFilter(validSeverancesFilter);
    }
  }, [validSeverancesFilter, severancesFilter, setSeverancesFilter]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Violation Letters and Severance/Seal Issuances */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Violation Letters and Severance/Seal Issuances
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenSeverancesLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month
              const monthlyData = {};
              
              severancesData.forEach(record => {
                // Use Certified Letter Date for violations
                const dateColumn = record['Certified Letter Date'] !== undefined ? 'Certified Letter Date' :
                                 record.certified_letter_date !== undefined ? 'certified_letter_date' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        totalViolations: 0,
                        severancesIssued: 0
                      };
                    }
                    
                    monthlyData[monthKey].totalViolations++;
                    
                    // Check for Issue Date (severances/seals issued)
                    const issueDateColumn = record['Issue Date'] !== undefined ? 'Issue Date' :
                                          record.issue_date !== undefined ? 'issue_date' : null;
                    
                    if (issueDateColumn && record[issueDateColumn] && record[issueDateColumn].toString().trim() !== '') {
                      monthlyData[monthKey].severancesIssued++;
                    }
                  }
                }
              });
              
              // Convert to array and sort by month
              return Object.values(monthlyData)
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Total Violations') {
                  return [`${value.toLocaleString()}`, 'Total'];
                } else if (name === 'Severances/Seals Issued') {
                  return [`${value.toLocaleString()}`, 'Severances/Seals'];
                }
                return [`${value.toLocaleString()}`, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenSeverancesLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="totalViolations"
              fill="#2196f3"
              name="Total Violations"
              hide={hiddenSeverancesLines.has('totalViolations')}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="severancesIssued"
              stroke="#e91e63"
              strokeWidth={2}
              name="Severances/Seals Issued"
              dot={{ fill: '#e91e63', strokeWidth: 2, r: 4 }}
              hide={hiddenSeverancesLines.has('severancesIssued')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Chart 2: Violation Resolution Trends */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Violation Resolution Trends
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenSeveranceVolumeLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month
              const monthlyData = {};
              
              severancesData.forEach(record => {
                // Use Certified Letter Date for violations
                const dateColumn = record['Certified Letter Date'] !== undefined ? 'Certified Letter Date' :
                                 record.certified_letter_date !== undefined ? 'certified_letter_date' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        totalViolations: 0,
                        resolvedViolations: 0,
                        totalDaysToResolve: 0,
                        resolvedCount: 0
                      };
                    }
                    
                    monthlyData[monthKey].totalViolations++;
                    
                    // Check if resolved (has Violation Resolved Date)
                    const resolvedDateColumn = record['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                                             record.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
                    
                    if (resolvedDateColumn && record[resolvedDateColumn] && record[resolvedDateColumn].toString().trim() !== '') {
                      monthlyData[monthKey].resolvedViolations++;
                      
                      // Calculate days to resolve
                      const resolvedDate = new Date(record[resolvedDateColumn]);
                      if (!isNaN(resolvedDate.getTime())) {
                        const timeDiff = resolvedDate.getTime() - date.getTime();
                        const days = timeDiff / (1000 * 60 * 60 * 24);
                        if (days >= 0) {
                          monthlyData[monthKey].totalDaysToResolve += days;
                          monthlyData[monthKey].resolvedCount++;
                        }
                      }
                    }
                  }
                }
              });
              
              // Calculate percentages and averages with proper formatting
              Object.values(monthlyData).forEach(month => {
                month.resolutionPercentage = month.totalViolations > 0 ? ((month.resolvedViolations / month.totalViolations) * 100).toFixed(1) : 0;
                month.averageDaysToResolve = month.resolvedCount > 0 ? Math.round(month.totalDaysToResolve / month.resolvedCount) : 0;
              });
              
              // Convert to array and sort by month
              return Object.values(monthlyData)
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" tick={{ fill: '#ff9800' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#4caf50' }} />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Resolved Rate (%)') {
                  return [`${value}%`, 'Rate'];
                } else if (name === 'Days to Resolve') {
                  return [`${value}`, 'Days'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenSeveranceVolumeLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar
              yAxisId="right"
              dataKey="averageDaysToResolve"
              fill="#ff9800"
              name="Days to Resolve"
              hide={hiddenSeveranceVolumeLines.has('averageDaysToResolve')}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="resolutionPercentage"
              stroke="#4caf50"
              strokeWidth={2}
              name="Resolved Rate (%)"
              dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
              hide={hiddenSeveranceVolumeLines.has('resolutionPercentage')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Unresolved Violations - Only show if there's data for at least one filter */}
      {hasAnySeverances && (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }} data-table-title={
          validSeverancesFilter === 'all' ? 'All Violations' : 'Unresolved Violations'
      } data-table-type="main">
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {validSeverancesFilter === 'all' ? 'All Violations' : 'Unresolved Violations'}
            </Typography>
            <Tooltip title="Reset Table" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  onResetUnresolvedViolations();
                  resetSeverancesFilter();
                  resetHiddenSeverancesLines();
                  resetHiddenSeveranceVolumeLines();
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Only show toggle if both options are available */}
          {hasSeverancesUnresolved && hasSeverancesAll && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <ToggleButtonGroup
                value={validSeverancesFilter}
              exclusive
              onChange={(event, newFilter) => {
                if (newFilter !== null) {
                  setSeverancesFilter(newFilter);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="unresolved">
                Unresolved
              </ToggleButton>
              <ToggleButton value="all">
                All
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          )}
        </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortUnresolvedViolations('lease_name')}
                >
                  Lease Name
                  {unresolvedViolationsSortField === 'lease_name' && (
                    <span style={{ marginLeft: 8 }}>
                      {unresolvedViolationsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortUnresolvedViolations('certified_letter_date')}
                >
                  Certified Letter Date
                  {unresolvedViolationsSortField === 'certified_letter_date' && (
                    <span style={{ marginLeft: 8 }}>
                      {unresolvedViolationsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortUnresolvedViolations('violation_resolved_date')}
                >
                  Violation Resolved Date
                  {unresolvedViolationsSortField === 'violation_resolved_date' && (
                    <span style={{ marginLeft: 8 }}>
                      {unresolvedViolationsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortUnresolvedViolations('issue_reason')}
                >
                  Issue Reason
                  {unresolvedViolationsSortField === 'issue_reason' && (
                    <span style={{ marginLeft: 8 }}>
                      {unresolvedViolationsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortUnresolvedViolations('issue_date')}
                >
                  Issue Date
                  {unresolvedViolationsSortField === 'issue_date' && (
                    <span style={{ marginLeft: 8 }}>
                      {unresolvedViolationsSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Filter violations based on user selection
                const filteredViolations = severancesData.filter(record => {
                  if (validSeverancesFilter === 'all') {
                    return true; // Show all violations
                  }
                  
                  // Filter for unresolved violations (no Violation Resolved Date)
                  const resolvedDateColumn = record['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                                           record.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
                  
                  return !resolvedDateColumn || !record[resolvedDateColumn] || record[resolvedDateColumn].toString().trim() === '';
                });

                // Sort the filtered violations
                filteredViolations.sort((a, b) => {
                  let aValue, bValue;
                  
                  switch (unresolvedViolationsSortField) {
                    case 'lease_name':
                      aValue = (a['Lease Name'] || a.lease_name || '').toLowerCase();
                      bValue = (b['Lease Name'] || b.lease_name || '').toLowerCase();
                      break;
                    case 'certified_letter_date':
                      aValue = new Date(a['Certified Letter Date'] || a.certified_letter_date || '');
                      bValue = new Date(b['Certified Letter Date'] || b.certified_letter_date || '');
                      break;
                    case 'violation_resolved_date':
                      aValue = new Date(a['Violation Resolved Date'] || a.violation_resolved_date || '');
                      bValue = new Date(b['Violation Resolved Date'] || b.violation_resolved_date || '');
                      break;
                    case 'issue_reason':
                      aValue = (a['Issue Reason'] || a.issue_reason || '').toLowerCase();
                      bValue = (b['Issue Reason'] || b.issue_reason || '').toLowerCase();
                      break;
                    case 'issue_date':
                      aValue = new Date(a['Issue Date'] || a.issue_date || '');
                      bValue = new Date(b['Issue Date'] || b.issue_date || '');
                      break;
                    default:
                      aValue = new Date(a['Certified Letter Date'] || a.certified_letter_date || '');
                      bValue = new Date(b['Certified Letter Date'] || b.certified_letter_date || '');
                  }
                  
                  if (unresolvedViolationsSortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                  } else {
                    return aValue < bValue ? 1 : -1;
                  }
                });

                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = filteredViolations.slice(startIndex, endIndex);

                return paginatedData.map((record, index) => (
                  <TableRow key={startIndex + index}>
                    <TableCell>
                      {record['Lease Name'] || record.lease_name || null}
                    </TableCell>
                    <TableCell>
                      {record['Certified Letter Date'] || record.certified_letter_date || null}
                    </TableCell>
                    <TableCell>
                      {record['Violation Resolved Date'] || record.violation_resolved_date || null}
                    </TableCell>
                    <TableCell>
                      {record['Issue Reason'] || record.issue_reason || null}
                    </TableCell>
                    <TableCell>
                      {record['Issue Date'] || record.issue_date || null}
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {(() => {
          // Calculate total rows for pagination
          const filteredViolations = severancesData.filter(record => {
            if (validSeverancesFilter === 'all') {
              return true;
            }
            const resolvedDateColumn = record['Violation Resolved Date'] !== undefined ? 'Violation Resolved Date' :
                                     record.violation_resolved_date !== undefined ? 'violation_resolved_date' : null;
            return !resolvedDateColumn || !record[resolvedDateColumn] || record[resolvedDateColumn].toString().trim() === '';
          });
          
          return (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredViolations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  justifyContent: 'center',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
              }}
            />
          );
        })()}
      </Paper>
      )}
    </Box>
  );
};

export default SeverancesCharts;